import React, { useMemo, useState } from "react";
import { Plus, Building2, Users2, CircleDot, MoreHorizontal, MapPin, Trash2 } from "lucide-react";
import { PageContainer } from "@/components/layout/PageContainer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { useAuth, useWorkspace } from "@/lib/store";
import { useLocalization } from "@/lib/localization";
import { createBranch, deleteBranch, updateBranch } from "@/lib/shopApi";
import { requirePreciseBrowserLocation } from "@/lib/location";
import { getBranchParityCopy } from "@/lib/branchParityCopy";
import { toast } from "sonner";
import { z } from "zod";

function createBranchCreateSchema(copy) {
  return z.object({
    name: z.string()
      .trim()
      .min(2, copy.branchNameTooShort)
      .max(80, copy.branchNameTooLong),
  });
}

function createBranchUpdateSchema(copy) {
  return createBranchCreateSchema(copy).extend({
    isActive: z.boolean(),
  });
}

function branchRoleLabel(copy, role) {
  const map = {
    Admin: copy.roleAdmin,
    Member: copy.roleMember,
    Owner: copy.roleOwner,
  };
  return map[role] ?? role;
}

async function captureBranchLocation() {
  const location = await requirePreciseBrowserLocation();
  return {
    ...location,
    declaredLatitude: location.latitude,
    declaredLongitude: location.longitude,
    locationSource: "device",
  };
}

function branchLocationSummary(branch, fallback) {
  if (
    branch?.declaredLatitude === null ||
    branch?.declaredLatitude === undefined ||
    branch?.declaredLongitude === null ||
    branch?.declaredLongitude === undefined
  ) {
    return fallback;
  }
  return `${Number(branch.declaredLatitude).toFixed(4)}, ${Number(branch.declaredLongitude).toFixed(4)}`;
}

export default function Branches() {
  const { session, updateSession } = useAuth();
  const { currentBranch, switchBranch, branches, reload } = useWorkspace();
  const { language, messages } = useLocalization();
  const copy = messages.branchesPage;
  const locationCopy = messages.settings;
  const parityCopy = getBranchParityCopy(language);
  const [createOpen, setCreateOpen] = useState(false);
  const [branchName, setBranchName] = useState("");
  const [branchNameError, setBranchNameError] = useState("");
  const [creating, setCreating] = useState(false);
  const [createLocation, setCreateLocation] = useState(null);
  const [capturingLocation, setCapturingLocation] = useState(false);
  const [editingBranch, setEditingBranch] = useState(null);
  const [editForm, setEditForm] = useState({ isActive: true, name: "" });
  const [editError, setEditError] = useState("");
  const [editLocation, setEditLocation] = useState(undefined);
  const [updating, setUpdating] = useState(false);
  const [removing, setRemoving] = useState(false);

  const activeCount = useMemo(() => branches.filter((b) => b.isActive).length, [branches]);
  const branchCreateSchema = useMemo(() => createBranchCreateSchema(copy), [copy]);
  const branchUpdateSchema = useMemo(() => createBranchUpdateSchema(copy), [copy]);

  return (
    <PageContainer>
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{copy.title}</h1>
          <p className="text-muted-foreground text-sm mt-1">{copy.locationsSummary(branches.length, activeCount)}</p>
        </div>
        <Button data-testid="branches-create-open" onClick={() => setCreateOpen(true)} className="h-9">
          <Plus className="h-4 w-4 mr-1" /> {copy.newBranch}
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {branches.map((b) => {
          const isCurrent = b.id === currentBranch?.id;
          return (
            <div
              key={b.id}
              data-testid={`branch-card-${b.id}`}
              className={`rounded-xl border bg-card p-5 relative overflow-hidden ${isCurrent ? "border-primary/40 ring-1 ring-primary/20" : "border-border"}`}
            >
              {isCurrent && (
                <div className="absolute top-3 right-3">
                  <Badge className="bg-primary text-primary-foreground text-[10px] uppercase tracking-wider">{copy.current}</Badge>
                </div>
              )}
              <div className="flex items-start gap-3">
                <div className={`h-11 w-11 rounded-md flex items-center justify-center shrink-0 ${b.isActive ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground"}`}>
                  <Building2 className="h-5 w-5" />
                </div>
                <div className="min-w-0">
                  <div className="font-semibold text-base tracking-tight truncate">{b.name}</div>
                  <div className="mt-0.5 flex items-center gap-1 text-xs text-muted-foreground">
                    <MapPin className="h-3 w-3" />
                    {branchLocationSummary(b, locationCopy.noBranchLocation)}
                  </div>
                </div>
              </div>

              <div className="mt-4 flex items-center gap-2">
                <span className={`inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-semibold px-2 py-1 rounded-full ${
                  b.isActive ? "bg-success/15 text-success" : "bg-muted text-muted-foreground"
                }`}>
                  <CircleDot className="h-2.5 w-2.5" />
                  {b.isActive ? copy.active : copy.inactive}
                </span>
                <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
                  {branchRoleLabel(copy, b.role)}
                </span>
              </div>

              <div className="mt-5 grid grid-cols-1 gap-2 text-center">
                <MiniStat icon={<Users2 className="h-3.5 w-3.5" />} label={copy.staff} value={b.staffCount} />
              </div>

              <div className="mt-5 flex items-center gap-2">
                <Button
                  data-testid={`branch-switch-${b.id}`}
                  size="sm"
                  variant={isCurrent ? "outline" : "default"}
                  className="flex-1"
                  disabled={isCurrent || !b.isActive}
                  onClick={async () => {
                    try {
                      await switchBranch(b.id);
                      toast.success(copy.switchedToBranch(b.name));
                    } catch (error) {
                      toast.error(error instanceof Error ? error.message : copy.couldNotUpdateBranch);
                    }
                  }}
                >
                  {isCurrent ? copy.currentBranch : copy.switchToBranch}
                </Button>
                <Button
                  data-testid={`branch-edit-${b.id}`}
                  variant="outline"
                  size="icon"
                  className="h-9 w-9"
                  onClick={() => {
                    setEditingBranch(b);
                    setEditForm({ isActive: Boolean(b.isActive), name: b.name });
                    setEditLocation(undefined);
                    setEditError("");
                  }}
                >
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </div>
            </div>
          );
        })}
      </div>

      <Dialog open={createOpen} onOpenChange={(open) => {
        setCreateOpen(open);
        if (!open) {
          setBranchNameError("");
          setCreateLocation(null);
        }
      }}>
          <DialogContent>
          <DialogHeader>
            <DialogTitle>{copy.createNewBranch}</DialogTitle>
            <DialogDescription>{copy.createDescription}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <div className="text-[11px] uppercase tracking-wider font-semibold text-muted-foreground mb-1.5">{copy.branchName}</div>
              <Input
                data-testid="branch-create-name"
                placeholder={copy.branchNamePlaceholder}
                className={`h-10 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:border-input ${branchNameError ? "border-destructive" : ""}`}
                value={branchName}
                onChange={(e) => {
                  setBranchName(e.target.value);
                  setBranchNameError("");
                }}
              />
              {branchNameError ? <p className="mt-1.5 text-xs font-medium text-destructive">{branchNameError}</p> : null}
            </div>
            <div className="rounded-md border border-border px-3 py-3">
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-sm font-semibold">{locationCopy.branchLocation}</div>
                  <div className="mt-0.5 truncate text-xs text-muted-foreground">
                    {branchLocationSummary(createLocation, locationCopy.noBranchLocation)}
                  </div>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={capturingLocation}
                  onClick={async () => {
                    setCapturingLocation(true);
                    try {
                      setCreateLocation(await captureBranchLocation());
                      toast.success(parityCopy.locationAttached);
                    } catch (error) {
                      toast.error(error instanceof Error ? error.message : locationCopy.updateLocationFailed);
                    } finally {
                      setCapturingLocation(false);
                    }
                  }}
                >
                  <MapPin className="mr-1 h-4 w-4" />
                  {createLocation ? locationCopy.updateLocationRefresh : locationCopy.updateLocation}
                </Button>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setCreateOpen(false)}>{copy.cancel}</Button>
            <Button
              data-testid="branch-create-confirm"
              disabled={creating || !branchName.trim()}
              onClick={async () => {
                if (!session?.sessionId) return;
                const parsed = branchCreateSchema.safeParse({ name: branchName });
                if (!parsed.success) {
                  setBranchNameError(parsed.error.issues[0]?.message || copy.enterValidBranchName);
                  return;
                }
                setCreating(true);
                try {
                  const response = await createBranch(session.sessionId, {
                    location: createLocation || undefined,
                    name: parsed.data.name,
                  });
                  if (response?.session) updateSession({ ...session, ...response.session });
                  await reload();
                  toast.success(copy.branchCreated);
                  setBranchName("");
                  setCreateLocation(null);
                  setBranchNameError("");
                  setCreateOpen(false);
                } catch (error) {
                  toast.error(error?.message || copy.couldNotCreateBranch);
                } finally {
                  setCreating(false);
                }
              }}
            >
              {copy.createBranch}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editingBranch} onOpenChange={(open) => {
        if (!open) {
          setEditingBranch(null);
          setEditError("");
        }
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{copy.updateBranch}</DialogTitle>
            <DialogDescription>{copy.updateDescription}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <div className="text-[11px] uppercase tracking-wider font-semibold text-muted-foreground mb-1.5">{copy.branchName}</div>
              <Input
                data-testid="branch-edit-name"
                placeholder={copy.branchNamePlaceholder}
                className={`h-10 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:border-input ${editError ? "border-destructive" : ""}`}
                value={editForm.name}
                onChange={(e) => {
                  setEditForm((prev) => ({ ...prev, name: e.target.value }));
                  setEditError("");
                }}
              />
              {editError ? <p className="mt-1.5 text-xs font-medium text-destructive">{editError}</p> : null}
            </div>

            <div className="rounded-md border border-border px-3 py-3">
              <div className="text-sm font-semibold">{locationCopy.branchLocation}</div>
              <div className="mt-0.5 text-xs text-muted-foreground">
                {branchLocationSummary(editLocation === undefined ? editingBranch : editLocation, locationCopy.noBranchLocation)}
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={capturingLocation}
                  onClick={async () => {
                    setCapturingLocation(true);
                    try {
                      setEditLocation(await captureBranchLocation());
                      toast.success(parityCopy.locationAttached);
                    } catch (error) {
                      toast.error(error instanceof Error ? error.message : locationCopy.updateLocationFailed);
                    } finally {
                      setCapturingLocation(false);
                    }
                  }}
                >
                  <MapPin className="mr-1 h-4 w-4" />
                  {locationCopy.updateLocationRefresh}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setEditLocation({
                    declaredLatitude: null,
                    declaredLongitude: null,
                    locationSource: "unknown",
                  })}
                >
                  {parityCopy.removeLocation}
                </Button>
              </div>
            </div>

            <div className="flex items-center justify-between gap-4 rounded-md border border-border px-3 py-3">
              <div>
                <div className="text-sm font-semibold">{copy.activeBranch}</div>
                <div className="text-xs text-muted-foreground">{copy.activeBranchDescription}</div>
              </div>
              <Switch
                data-testid="branch-edit-active"
                checked={editForm.isActive}
                onCheckedChange={(checked) => {
                  if (!checked && editingBranch?.isActive && activeCount <= 1) {
                    toast.error(parityCopy.activeRequired);
                    return;
                  }
                  setEditForm((prev) => ({ ...prev, isActive: checked }));
                }}
                className="focus-visible:ring-0 focus-visible:ring-offset-0"
              />
            </div>
          </div>
          <DialogFooter className="sm:justify-between">
            <Button
              variant="outline"
              className="text-destructive hover:text-destructive"
              disabled={removing}
              onClick={async () => {
                if (!session?.sessionId || !editingBranch?.id) return;
                if (session.role !== "Owner") {
                  toast.error(parityCopy.ownerRequired);
                  return;
                }
                const hasRecords = Number(editingBranch.staffCount || 0) > 0;
                if (!hasRecords && branches.length <= 1) {
                  toast.error(parityCopy.branchRequired);
                  return;
                }
                if (hasRecords && !editingBranch.isActive) {
                  toast.error(parityCopy.inactiveAlready(editingBranch.name));
                  return;
                }
                if (hasRecords && editingBranch.isActive && activeCount <= 1) {
                  toast.error(parityCopy.activeRequired);
                  return;
                }
                setRemoving(true);
                try {
                  const response = hasRecords
                    ? await updateBranch(session.sessionId, editingBranch.id, { isActive: false })
                    : await deleteBranch(session.sessionId, editingBranch.id);
                  if (response?.session) updateSession({ ...session, ...response.session });
                  await reload();
                  toast.success(hasRecords ? copy.branchUpdated : parityCopy.delete);
                  setEditingBranch(null);
                } catch (error) {
                  toast.error(error instanceof Error ? error.message : copy.couldNotUpdateBranch);
                } finally {
                  setRemoving(false);
                }
              }}
            >
              <Trash2 className="mr-1 h-4 w-4" />
              {Number(editingBranch?.staffCount || 0) > 0 ? parityCopy.deactivate : parityCopy.delete}
            </Button>
            <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setEditingBranch(null)}>{copy.cancel}</Button>
            <Button
              data-testid="branch-edit-confirm"
              disabled={updating}
              onClick={async () => {
                if (!session?.sessionId || !editingBranch?.id) return;
                const parsed = branchUpdateSchema.safeParse(editForm);
                if (!parsed.success) {
                  setEditError(parsed.error.issues[0]?.message || copy.enterValidBranchName);
                  return;
                }
                setUpdating(true);
                try {
                  const response = await updateBranch(session.sessionId, editingBranch.id, {
                    isActive: parsed.data.isActive,
                    location: editLocation,
                    name: parsed.data.name,
                  });
                  if (response?.session) updateSession({ ...session, ...response.session });
                  await reload();
                  toast.success(copy.branchUpdated);
                  setEditingBranch(null);
                  setEditError("");
                } catch (error) {
                  toast.error(error?.message || copy.couldNotUpdateBranch);
                } finally {
                  setUpdating(false);
                }
              }}
            >
              {copy.saveChanges}
            </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageContainer>
  );
}

function MiniStat({ icon, label, value }) {
  return (
    <div className="rounded-md border border-border p-2.5">
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold flex items-center justify-center gap-1">
        {icon}
        {label}
      </div>
      <div className="font-mono text-sm font-semibold mt-1 tabular-nums truncate">{value}</div>
    </div>
  );
}
